import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Clock,
  Timer,
  TrendingDown,
  CheckCircle,
  XCircle,
  X,
  UserCheck,
  Filter,
  Shield,
  AlignLeft
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import { CustomSelect, CustomDatePicker, CustomTimeInput } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { formatPointageDate, formatPointageMoney, calculateLatenessDeduction, getLateThresholdTime, SAUDI_DEFAULT_TIMETRACKING_CONFIG } from './pointageHelpers';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const NS = 'modules.pointage.pages.latenessPage';

const SEVERITY_VALUES = {
  light: 'Light',
  medium: 'Medium',
  severe: 'Severe',
};

const LEGACY_SEVERITY_MAP = {
  Légère: SEVERITY_VALUES.light,
  Moyenne: SEVERITY_VALUES.medium,
  Grave: SEVERITY_VALUES.severe,
};

const LatenessPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`${NS}.${key}`, opts);

  const getStatusLabel = (status) => {
    const labels = {
      justified: p('status_justified'),
      unjustified: p('status_unjustified')
    };
    return labels[status] || p('em_dash');
  };

  const defaultExpectedTime = SAUDI_DEFAULT_TIMETRACKING_CONFIG.workDayStart;
  const graceMinutes = SAUDI_DEFAULT_TIMETRACKING_CONFIG.latenessThreshold;

  const formatDeduction = (late) => {
    const mins = late.delay_minutes || late.delayMinutes || 0;
    if (!mins || late.status === 'justified') return '—';
    const amount = late.deduction_amount ?? late.deductionAmount
      ?? calculateLatenessDeduction(late.hourly_rate || late.hourlyRate || 0, mins);
    return amount > 0 ? formatPointageMoney(amount) : '—';
  };

  const getSeverityLabel = (severity) => {
    const normalized = LEGACY_SEVERITY_MAP[severity] || severity;
    const entry = Object.entries(SEVERITY_VALUES).find(([, value]) => value === normalized);
    if (entry) return p(`severity.${entry[0]}`);
    return severity;
  };
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewTab, setMainViewTab] = useState('all');
  const [quickFilterTab, setQuickFilterTab] = useState('all');
  
  // Employee options — live HR (/hr/employees), id-based linkage
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLateness, setSelectedLateness] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  // Data
  const [lateness, setLateness] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    justified: 0,
    unjustified: 0,
    totalMinutes: 0,
    avgDelay: 0
  });
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeId: null,
    date: '',
    arrivalTime: '',
    expectedTime: '',
    delayMinutes: 0,
    justification: '',
    status: '',
    severity: '',
    sanction: '',
    notes: ''
  });

  const [formTab, setFormTab] = useState('general');

  const LATENESS_FORM_FIELD_COUNT = 9;

  const latenessFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_basic_info'), icon: UserCheck },
      { id: 'details', label: p('section_justification'), icon: FileText, iconClass: 'text-purple-400' },
    ],
  }, LATENESS_FORM_FIELD_COUNT), [p]);

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchLateness();
    fetchStats();
  }, [dateRange]);

  const fetchLateness = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const data = await localApiAdapter.get('/timetracking/lateness', params);
      setLateness(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error fetching lateness:', error);
      setLateness([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/lateness/stats');
      setStats({
        total: data.total || 0,
        justified: data.justified || 0,
        unjustified: data.unjustified || 0,
        totalMinutes: data.totalMinutes || 0,
        avgDelay: data.avgDelay || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        setLoading(true);
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/timetracking/lateness/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        await fetchLateness();
        await fetchStats();
        setSelectedRecords([]);
      } catch (error) {
        console.error('Error deleting lateness:', error);
        notifyError(showNotify, p('error_bulk_delete'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        employee_id: formData.employeeId,
        employee_name: formData.employeeName
          || resolvePointageEmployeeDisplayName(formData.employeeId, employeeOptions, ''),
        date: formData.date,
        expected_time: formData.expectedTime,
        arrival_time: formData.arrivalTime,
        justification: formData.justification,
        status: formData.status
      };

      if (selectedLateness) {
        await localApiAdapter.put(`/timetracking/lateness/${selectedLateness.id}`, payload);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/timetracking/lateness', payload);
        notifyCreated(showNotify, p('title'));
      }

      await fetchLateness();
      await fetchStats();
      resetForm();
    } catch (error) {
      console.error('Error saving lateness:', error);
      notifyError(showNotify, p('error_save')); return;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (late) => {
    setSelectedLateness(late);
    setFormData({
      employeeName: late.employee_name || late.employeeName,
      employeeId: late.employee_id != null ? String(late.employee_id) : '',
      date: late.date,
      arrivalTime: late.arrival_time || late.arrivalTime,
      expectedTime: late.expected_time || late.expectedTime,
      delayMinutes: late.delay_minutes || late.delayMinutes,
      justification: late.justification || '',
      status: late.status,
      severity: late.severity || '',
      sanction: late.sanction || '',
      notes: late.notes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/timetracking/lateness/${id}`);
        notifyDeleted(showNotify, p('title'));
        await fetchLateness();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting lateness:', error);
        notifyError(showNotify, p('error_delete'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/timetracking/lateness/${id}`, { status: newStatus });
      await fetchLateness();
      await fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: 'justified', label: p('status_justified'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'unjustified', label: p('status_unjustified'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ], [p]);

  const statusColumnFilterOptions = useMemo(
    () => clickableStatusOptions.map(({ value, label }) => ({ value, label })),
    [clickableStatusOptions],
  );

  const handleView = (late) => {
    setSelectedLateness(late);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ employeeName: '', employeeId: null, date: '', arrivalTime: '', expectedTime: '', delayMinutes: 0, justification: '', status: '', severity: '', sanction: '', notes: '' });
    setSelectedLateness(null);
    setFormTab('general');
    setShowModal(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'justified': 'bg-green-500/20 text-green-300 border-green-500/30',
      'unjustified': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['unjustified']}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  const baseFilteredLateness = useMemo(() => lateness.filter(late => {
    const matchesSearch = !searchTerm ||
      (late.employee_name || late.employeeName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      late.justification?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || late.status === statusFilter;
    const matchesMainView = mainViewTab === 'all' ||
      (mainViewTab === 'justified' && late.status === 'justified') ||
      (mainViewTab === 'unjustified' && late.status === 'unjustified');
    const delayMins = late.delay_minutes || late.delayMinutes || 0;
    const matchesQuickFilter = quickFilterTab === 'all' ||
      (quickFilterTab === 'less5' && delayMins > 0 && delayMins < 5) ||
      (quickFilterTab === '5to15' && delayMins >= 5 && delayMins <= graceMinutes) ||
      (quickFilterTab === '15to30' && delayMins > graceMinutes && delayMins < 30) ||
      (quickFilterTab === 'more30' && delayMins >= 30);
    return matchesSearch && matchesStatus && matchesMainView && matchesQuickFilter;
  }), [lateness, searchTerm, statusFilter, mainViewTab, quickFilterTab, graceMinutes]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('employee', (l) => l.employee_name || l.employeeName || ''),
    textColumnRule('date', (l) => formatPointageDate(l.date) || ''),
    textColumnRule('expected_time', (l) => l.expected_time || l.expectedTime || getLateThresholdTime()),
    textColumnRule('arrival_time', (l) => l.arrival_time || l.arrivalTime || l.check_in || l.checkIn || ''),
    textColumnRule('delay', (l) => String(l.delay ?? l.delay_minutes ?? l.delayMinutes ?? '')),
    textColumnRule('deduction', (l) => formatPointageMoney(l.deduction_amount ?? l.deductionAmount ?? calculateLatenessDeduction(l.hourly_rate || l.hourlyRate || 0, l.delay_minutes || l.delayMinutes || 0))),
    exactColumnRule('status', (l) => l.status || ''),
  ], []);

  const filteredLateness = useMemo(
    () => applyColumnFilters(baseFilteredLateness, colFilters, columnFilterRules),
    [baseFilteredLateness, colFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredLateness);

  // Distribution stats
  const justifiedCount = filteredLateness.filter(l => l.status === 'justified').length;
  const unjustifiedCount = filteredLateness.filter(l => l.status === 'unjustified').length;
  const totalFiltered = filteredLateness.length;

  const totalDeductions = filteredLateness.reduce((sum, late) => {
    if (late.status === 'justified') return sum;
    const mins = late.delay_minutes || late.delayMinutes || 0;
    const amount = late.deduction_amount ?? late.deductionAmount
      ?? calculateLatenessDeduction(late.hourly_rate || late.hourlyRate || 0, mins);
    return sum + (Number(amount) || 0);
  }, 0);

  const latenessKpiCards = [
    { key: 'stat_total', value: stats.total, icon: AlertCircle, color: 'from-blue-500 to-blue-600' },
    { key: 'stat_justified', value: stats.justified, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'stat_unjustified', value: stats.unjustified, icon: XCircle, color: 'from-red-500 to-red-600' },
    { key: 'stat_total_minutes', value: p('minutes_value', { minutes: stats.totalMinutes }), icon: Clock, color: 'from-orange-500 to-orange-600' },
    { key: 'stat_total_deductions', value: formatPointageMoney(totalDeductions), icon: TrendingDown, color: 'from-red-500 to-rose-600' },
  ];

  const mainTabs = [
    { id: 'all' },
    { id: 'justified' },
    { id: 'unjustified' }
  ];

  const quickFilterButtons = [
    { id: 'all' },
    { id: 'less5' },
    { id: '5to15' },
    { id: '15to30' },
    { id: 'more30' }
  ];

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'employee', labelKey: 'col_employee' },
    { key: 'date', labelKey: 'col_date' },
    { key: 'expected_time', labelKey: 'col_expected_time' },
    { key: 'arrival_time', labelKey: 'col_arrival_time' },
    { key: 'delay', labelKey: 'col_delay' },
    { key: 'deduction', labelKey: 'col_deduction' },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <div className="space-y-6 clients-container">
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
            <button onClick={() => { fetchLateness(); fetchStats(); }} className="refresh-button ml-4" title={p('refresh')}>
              <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
          <p className="text-white/40 text-xs mt-1">
            {p('grace_period_hint', { minutes: graceMinutes, time: getLateThresholdTime() })}
          </p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({
            count: selectedRecords.length,
            onDelete: handleBulkDelete,
            label: `${t('common.delete')} (${selectedRecords.length})`,
            title: t('common.delete', { defaultValue: 'حذف' }),
          })}
          <button
            type="button"
            onClick={() => {
              setSelectedLateness(null);
              setFormData({
                employeeName: '',
                employeeId: null,
                date: '',
                arrivalTime: '',
                expectedTime: defaultExpectedTime,
                delayMinutes: 0,
                justification: '',
                status: '',
                severity: '',
                sanction: '',
                notes: ''
              });
              setFormTab('general');
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_lateness')}</span>
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {latenessKpiCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group/card">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.key)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced options */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          {/* Main View Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-4">
            <h3 className="text-white font-semibold mb-3 text-sm">{p('main_view_title')}</h3>
            <div className="flex gap-2">
              {mainTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setMainViewTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    mainViewTab === tab.id
                      ? 'bg-blue-500/30 text-blue-200 border border-blue-400/50'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {p(`tab_${tab.id}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-4">
            <h3 className="text-white font-semibold mb-3 text-sm">{p('quick_filters_title')}</h3>
            <div className="flex gap-2 flex-wrap">
              {quickFilterButtons.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setQuickFilterTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    quickFilterTab === tab.id
                      ? 'bg-orange-500/30 text-orange-200 border border-orange-400/50'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {p(`filter_${tab.id}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Distribution Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p('dist_justified')}</p>
                  <p className="text-2xl font-bold text-green-300 mt-1">
                    {totalFiltered > 0 ? Math.round((justifiedCount / totalFiltered) * 100) : 0}%
                  </p>
                  <p className="text-white/50 text-xs mt-1">{justifiedCount} / {totalFiltered}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-green-400" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p('dist_unjustified')}</p>
                  <p className="text-2xl font-bold text-red-300 mt-1">
                    {totalFiltered > 0 ? Math.round((unjustifiedCount / totalFiltered) * 100) : 0}%
                  </p>
                  <p className="text-white/50 text-xs mt-1">{unjustifiedCount} / {totalFiltered}</p>
                </div>
                <XCircle className="h-12 w-12 text-red-400" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Toggle Advanced Options Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl px-6 py-2 text-white font-medium transition-all flex items-center gap-2"
        >
          <span>{t('common.advanced_options')}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Data Table with Integrated Header */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        

        

        {/* Table or Card View */}
        {viewMode === 'table' ? (
          // Table View
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('employee') && (
                    <TableColumnHeaderFilter columnKey="employee" label={p('col_employee')} value={colFilters.employee ?? ''} onChange={(v) => setColFilter('employee', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('date') && (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={colFilters.date ?? ''} onChange={(v) => setColFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('expected_time') && (
                    <TableColumnHeaderFilter columnKey="expected_time" label={p('col_expected_time')} value={colFilters.expected_time ?? ''} onChange={(v) => setColFilter('expected_time', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('arrival_time') && (
                    <TableColumnHeaderFilter columnKey="arrival_time" label={p('col_arrival_time')} value={colFilters.arrival_time ?? ''} onChange={(v) => setColFilter('arrival_time', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('delay') && (
                    <TableColumnHeaderFilter columnKey="delay" label={p('col_delay')} value={colFilters.delay ?? ''} onChange={(v) => setColFilter('delay', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('deduction') && (
                    <TableColumnHeaderFilter columnKey="deduction" label={p('col_deduction')} value={colFilters.deduction ?? ''} onChange={(v) => setColFilter('deduction', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {filteredLateness.length > 0 ? (
                  filteredLateness.map((late) => (
                    <tr key={late.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(late.id)}
                        onChange={() => handleSelectRecord(late.id)}
                      />
                      {isColVisible('employee') && (
                        <td className="px-6 py-4 text-white font-semibold">{resolvePointageEmployeeDisplayName(late.employee_id, employeeOptions, late.employee_name || late.employeeName)}</td>
                      )}
                      {isColVisible('date') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(late.date)}</td>
                      )}
                      {isColVisible('expected_time') && (
                        <td className="px-6 py-4 text-white/80">{late.expected_time || late.expectedTime}</td>
                      )}
                      {isColVisible('arrival_time') && (
                        <td className="px-6 py-4 text-white/80">{late.arrival_time || late.arrivalTime}</td>
                      )}
                      {isColVisible('delay') && (
                        <td className="px-6 py-4 text-white font-bold">{p('minutes_value', { minutes: late.delay_minutes || late.delayMinutes })}</td>
                      )}
                      {isColVisible('deduction') && (
                        <td className="px-6 py-4 text-orange-300 font-medium">{formatDeduction(late)}</td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={late.status}
                              recordId={late.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => handleView(late)}
                            onEdit={() => handleEdit(late)}
                            onDelete={() => handleDelete(late.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                      {p('empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredLateness.map((late) => (
                <motion.div
                  key={late.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        {getStatusBadge(late.status)}
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-1 truncate">{late.employee_name || late.employeeName}</h3>
                      <p className="text-white/60 text-sm">{formatPointageDate(late.date)}</p>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(late)}
                        onEdit={() => handleEdit(late)}
                        onDelete={() => handleDelete(late.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_expected_time')}</span>
                      <span className="text-white font-semibold">{late.expected_time || late.expectedTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_arrival_time')}</span>
                      <span className="text-white font-semibold">{late.arrival_time || late.arrivalTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('card_delay')}</span>
                      <span className="text-white font-bold text-lg">{p('minutes_value', { minutes: late.delay_minutes || late.delayMinutes })}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-white/50 text-xs">{p('col_deduction')}</span>
                      <span className="text-orange-300 font-semibold">{formatDeduction(late)}</span>
                    </div>
                  </div>

                  {late.justification && (
                    <div className="bg-white/5 rounded-lg p-3 mt-4">
                      <p className="text-white/50 text-xs mb-1">{p('label_justification')}</p>
                      <p className="text-white text-sm">{late.justification}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal — même coquille « Ajouter un Pointage » */}
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={selectedLateness ? p('modal_edit_title') : p('modal_new_title')}
          icon={Clock}
          onSubmit
          formId="lateness-form"
          saveLabel={selectedLateness ? p('update') : t('common.save')}
          maxWidth="1000px"
          testId="lateness-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={latenessFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="lateness-form"
            onSubmit={handleSubmit}
            ariaLabel={selectedLateness ? p('modal_edit_title') : p('modal_new_title')}
          >
            {pointageFormTabVisible(latenessFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_employee_required')} />}</label>
                  <CustomSelect
                    value={formData.employeeId ? String(formData.employeeId) : ''}
                    onChange={(v) => {
                      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(v, employeeOptions);
                      setFormData({ ...formData, employeeId, employeeName });
                    }}
                    options={employeeOptions}
                    placeholder={p('employee_placeholder')}
                    searchable={true}
                    creatable={false}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_date_required')} />}</label>
                  <CustomDatePicker value={formData.date} onChange={(v) => setFormData({ ...formData, date: v })} placeholder={p('date_select_placeholder')} />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_status')}</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(v) => setFormData({ ...formData, status: v })}
                    options={[
                      { value: 'unjustified', label: p('status_unjustified') },
                      { value: 'justified', label: p('status_justified') }
                    ]}
                    placeholder={p('status_placeholder')}
                    searchable={true}
                    creatable={true}
                    autoSelect={false}
                  />
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_expected_time_required')} />}</label>
                  <CustomTimeInput value={formData.expectedTime} onChange={(v) => setFormData({ ...formData, expectedTime: v })} placeholder={p('time_placeholder')} required />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_arrival_time_required')} />}</label>
                  <CustomTimeInput value={formData.arrivalTime} onChange={(v) => setFormData({ ...formData, arrivalTime: v })} placeholder={p('time_placeholder')} required />
                </div>
              </div>
              </div>
            )}

            {pointageFormTabVisible(latenessFormTabs, formTab, 'details') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_justification_comment')}</label>
                  <textarea
                    value={formData.justification}
                    onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                    rows={4}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('justification_placeholder')}
                  />
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{p('label_severity')}</label>
                  <CustomSelect
                    value={formData.severity}
                    onChange={(v) => setFormData({ ...formData, severity: v })}
                    options={Object.entries(SEVERITY_VALUES).map(([key, value]) => ({ value, label: p(`severity.${key}`) }))}
                    placeholder={p('severity_placeholder')}
                    searchable={false}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_sanction')}</label>
                  <input type="text" value={formData.sanction} onChange={(e) => setFormData({ ...formData, sanction: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-white/40" placeholder={p('sanction_placeholder')} />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_notes')}</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40" placeholder={p('notes_placeholder')} />
                </div>
              </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}

      {/* View Modal */}
      {showViewModal && selectedLateness && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="640px"
          testId="lateness-view-modal"
        >
          <div className="space-y-4 space-y-4">
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{t('common.employee')}</p>
                <p className="text-white font-semibold text-lg">{selectedLateness.employee_name || selectedLateness.employeeName}</p>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{t('common.date')}</p>
                <p className={modalForm.viewValue}>{formatPointageDate(selectedLateness.date)}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('col_expected_time')}</p>
                  <p className={modalForm.viewValue}>{selectedLateness.expected_time || selectedLateness.expectedTime}</p>
                </div>
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('col_arrival_time')}</p>
                  <p className={modalForm.viewValue}>{selectedLateness.arrival_time || selectedLateness.arrivalTime}</p>
                </div>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('label_delay_duration')}</p>
                <p className="text-white font-bold text-2xl">{p('minutes_value', { minutes: selectedLateness.delay_minutes || selectedLateness.delayMinutes })}</p>
              </div>
              {selectedLateness.justification && (
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('label_justification')}</p>
                  <p className="text-white text-sm">{selectedLateness.justification}</p>
                </div>
              )}
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{t('common.status')}</p>
                {getStatusBadge(selectedLateness.status)}
              </div>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20 transition-all"
              >
                {t('common.close')}
              </button>
            </div>
        </QawaemErpViewModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default LatenessPage;


